import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useEvent } from '../contexts/EventContext';
import { useGroupNavigation } from '../hooks/useGroupNavigation';
import { useParams } from 'react-router-dom';
import { ReservationWithDetails, Person } from '../types';
import { apiService } from '../services/api';
import { WarningIcon, ClipboardTextIcon, XIcon } from "@phosphor-icons/react";
import { NoEventState } from '../components/events';
import { ConfirmModal } from '../components/common';
import { useConfirmModal } from '../hooks/useConfirmModal';
import './pages.css';
import '../components/lists/reservations.css';

const MyReservationsPage: React.FC = () => {
  const { getUserMainPerson, isAuthenticated, currentGroupSlug, availableGroups } = useAuth();
  const { selectedEvent, events, isInitialized } = useEvent();
  const { navigateToGroup } = useGroupNavigation();
  const { groupSlug } = useParams<{ groupSlug: string }>();
  const activeGroupSlug = groupSlug || currentGroupSlug;
  const isAdmin = availableGroups.some(
    (group) => group.slug === activeGroupSlug && group.isAdmin
  );
  const [reservations, setReservations] = useState<ReservationWithDetails[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isOpen, config, isLoading: isConfirmLoading, showConfirm, handleConfirm, handleCancel } = useConfirmModal();
  const [mainPerson, setMainPerson] = useState<Person | null>(null);
  const [isOnlyUserWithList, setIsOnlyUserWithList] = useState<boolean>(false);

  useEffect(() => {
    if (isAuthenticated && selectedEvent) {
      loadMainPersonAndReservations();
    }
  }, [isAuthenticated, selectedEvent]);

  const loadMainPersonAndReservations = async () => {
    if (!selectedEvent) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      
      // Get the main person for the current user
      const person = await getUserMainPerson();
      if (!person) {
        setError('Persona principale non trovata');
        return;
      }

      setMainPerson(person);

      // Get event stats to check if user is the only one with a list
      const eventStatsResponse = await apiService.getEventListStats(selectedEvent.id);
      const eventStats = eventStatsResponse.stats;
      const mainPersonHasList = eventStatsResponse.mainPersonHasList;
      
      // Use the same logic as dashboard: if no other persons have stats AND user has a list
      const otherPersonsWithLists = Object.keys(eventStats).filter(personId => 
        Number(personId) !== person.id
      ).length;
      const isOnlyUser = mainPersonHasList && otherPersonsWithLists === 0;
            
      setIsOnlyUserWithList(isOnlyUser);

      // Load reservations for the main person filtered by selected event
      const eventReservations = await apiService.getMyReservationsForEvent(person.id, selectedEvent.id);
      
      setReservations(eventReservations);
    } catch (err) {
      setError('Errore nel caricamento delle prenotazioni');
      console.error('Error loading reservations:', err);
    } finally {
      setIsLoading(false);
    }
  };



  const handleCancelReservation = async (reservationId: number) => {
    const reservation = reservations.find(r => r.id === reservationId);
    if (!reservation) return;

    const confirmed = await showConfirm({
      title: 'Annulla prenotazione',
      message: `Sei sicuro di voler annullare la prenotazione di "${reservation.listItem.name}"?`,
      confirmText: 'Annulla prenotazione',
      confirmVariant: 'warning'
    });

    if (!confirmed) return;

    try {
      await apiService.cancelReservation(reservationId);
      setReservations(prev => prev.filter(r => r.id !== reservationId));
    } catch (err) {
      setError('Errore nell\'annullamento della prenotazione');
      console.error('Error canceling reservation:', err);
    }
  };

  const formatPrice = (price?: number) => {
    if (!price) return null;
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
    }).format(price);
  };

  if (isInitialized && !selectedEvent) {
    return (
      <div className="my-reservations-page">
        <NoEventState
          isAdmin={isAdmin}
          hasExistingEvents={events.length > 0}
          onCreateEvent={
            isAdmin ? () => navigateToGroup("/admin#events") : undefined
          }
        />
      </div>
    );
  }

  if (!mainPerson && !isLoading) {
    return (
      <div className="my-reservations-page">
        <div className="error-container">
          <h2>Persona principale non trovata</h2>
          <p>Non è stata trovata la tua persona principale in questo gruppo.</p>
          <a href={groupSlug ? `/${groupSlug}/dashboard` : "/dashboard"} className="action-button secondary">
            Torna alla dashboard
          </a>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="my-reservations-page">
        <div className="loading-container">
          <p>Caricamento delle tue prenotazioni...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="my-reservations-page">
      <div className="modern-page-header">
        <div className="header-content">
          <div className="header-text">
            <h1>
              <ClipboardTextIcon size={32} weight="fill" />
              Le Mie Prenotazioni
            </h1>
            <p>Ecco i regali che hai riservato per <strong>{selectedEvent?.name || 'questo evento'}</strong></p>
          </div>
          <div className="header-decoration">
            <div className="decoration-circle"></div>
            <div className="decoration-circle"></div>
            <div className="decoration-circle"></div>
          </div>
        </div>
      </div>

      {error && (
        <div className="modern-error-message">
          <div className="error-content">
            <WarningIcon size={20} weight="fill" />
            <p>{error}</p>
            <button onClick={() => setError(null)} className="error-dismiss">
              <XIcon size={16} weight="bold" />
            </button>
          </div>
        </div>
      )}

      <div className="reservations-container">
        {reservations.length === 0 ? (
          <div className="empty-list">
            {(() => {
              return isOnlyUserWithList;
            })() ? (
              <>
                <p>Questo evento è tutto per te, solo gli altri possono prenotare i tuoi regali.</p>
                <p>Torna alla dashboard per gestire la tua lista regalo.</p>
              </>
            ) : (
              <>
                <p>Non hai ancora riservato nessun regalo per <strong>{selectedEvent?.name || 'questo evento'}</strong>.</p>
                <p>Torna alla dashboard per vedere chi vuole ricevere regali.</p>
              </>
            )}
          </div>
        ) : (
          <div className="reservations-list">
            {reservations.map((reservation) => (
              <div key={reservation.id} className="list-item reserved">
                <div className="item-content">
                  <div className="item-left">
                    <div className="item-header">
                      <h4 className="item-name">{reservation.listItem.name}</h4>
                      <div className="item-meta">
                        {reservation.listItem.link && (                        
                          <a 
                            href={reservation.listItem.link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="link-icon"
                            title="Visualizza prodotto"
                          >
                            ↗
                          </a>
                        )}
                        {reservation.listItem.price && (
                          <span className="item-price">{formatPrice(reservation.listItem.price)}</span>
                        )}
                      </div>
                    </div>
                    
                    {reservation.listItem.description && (
                      <div className="item-description">
                        <p>{reservation.listItem.description}</p>
                      </div>
                    )}

                    <div className="person-info">
                      <span className="person-badge">Per {reservation.listItem.person.name}</span>
                    </div>
                  </div>

                  <div className="item-right">
                    <div className="item-actions">
                      <button
                        onClick={() => handleCancelReservation(reservation.id)}
                        className="cancel-reservation-button"
                      >
                        Annulla prenotazione
                      </button>
                    </div>
                    <small className="reservation-date">
                      Riservato il {new Date(reservation.reservedAt).toLocaleDateString('it-IT')}
                    </small>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="page-actions">
        <a href={groupSlug ? `/${groupSlug}/dashboard` : "/dashboard"} className="action-button secondary">
          Torna alla dashboard
        </a>
      </div>

      <ConfirmModal
        isOpen={isOpen}
        title={config?.title}
        message={config?.message || ''}
        confirmText={config?.confirmText}
        cancelText={config?.cancelText}
        confirmVariant={config?.confirmVariant}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        isLoading={isConfirmLoading}
      />
    </div>
  );
};

export default MyReservationsPage;
